const merge = require('lodash/merge');
const uniq = require('lodash/uniq');
const TYPEBOX_OPTIONAL_SYMBOL = Symbol.for('TypeBox.Optional');

/**
 * Normalizes action values to a consistent array format.
 * Handles undefined, single values, and arrays.
 * @param {string|string[]|undefined} value - Action value(s) to normalize
 * @returns {string[]} Array of action strings (empty if no valid input)
 */
function normalizeActions(value) {
    if (!value) return [];
    return uniq(Array.isArray(value) ? value.filter(Boolean) : [value]).sort();
}

/**
 * Determines whether a property is required in all canonical write contexts.
 * Canonical contexts are create and update.
 *
 * @param {string} fieldName - Property key
 * @param {object} schema - Property schema with x-* metadata
 * @returns {boolean} True when property should be in relation required[]
 */
function isRequiredForAllCanonicalContexts(fieldName, schema) {
    if (!schema || typeof schema !== 'object') return false;
    // Foreign key fields (namespace:relation) should never be universally required
    if (fieldName.includes(':')) return false;
    if (!schema['x-required']) return false;

    const requiredActions = normalizeActions(schema['x-requiredActions']);
    const actionScope = requiredActions.length ? requiredActions : normalizeActions(schema['x-forAction']);

    // No action scope means required globally
    if (!actionScope.length) return true;
    if (actionScope.includes('*')) return true;

    return actionScope.includes('create') && actionScope.includes('update');
}

/**
 * Copies enumerable symbol-backed descriptors from source schemas to the merged schema.
 * TypeBox optional metadata is stored on symbols, and lodash merge does not preserve them.
 */
function copyEnumerableSymbolDescriptors(target, ...sources) {
    (sources || []).forEach(source => {
        if (!source || typeof source !== 'object') return;
        Object.getOwnPropertySymbols(source).forEach(symbol => {
            const descriptor = Object.getOwnPropertyDescriptor(source, symbol);
            if (!descriptor?.enumerable) return;
            Object.defineProperty(target, symbol, descriptor);
        });
    });
}

/**
 * Ensures the TypeBox optional symbol matches resolved x-optional state after merge.
 */
function normalizeTypeBoxOptionalSymbol(merged, existing = {}, incoming = {}) {
    if (!merged || typeof merged !== 'object') return;

    if (typeof merged['x-optional'] !== 'boolean') return;
    const shouldBeOptional = merged['x-optional'];
    if (!shouldBeOptional) {
        if (Object.prototype.hasOwnProperty.call(merged, TYPEBOX_OPTIONAL_SYMBOL)) {
            delete merged[TYPEBOX_OPTIONAL_SYMBOL];
        }
        return;
    }

    const incomingDescriptor = Object.getOwnPropertyDescriptor(incoming, TYPEBOX_OPTIONAL_SYMBOL);
    const existingDescriptor = Object.getOwnPropertyDescriptor(existing, TYPEBOX_OPTIONAL_SYMBOL);
    const preferredDescriptor = incomingDescriptor || existingDescriptor;

    Object.defineProperty(merged, TYPEBOX_OPTIONAL_SYMBOL, {
        value: preferredDescriptor?.value ?? 'Optional',
        enumerable: true,
        configurable: preferredDescriptor?.configurable ?? true,
        writable: preferredDescriptor?.writable ?? true
    });
}

/**
 * Merges schema flags (x-forAction, x-required, x-optional, x-requiredActions)
 * from two schemas with intelligent conflict resolution.
 *
 * Key behaviors:
 * - x-forAction arrays are combined (union)
 * - x-required is true if either schema requires it
 * - x-optional is derived from explicit requirements; unknown requiredness stays unspecified
 *
 * @param {object} existing - Existing schema with flags
 * @param {object} incoming - New schema to merge in
 * @returns {object} Merged schema with resolved flags
 */
function mergeSchemaFlags(existing = {}, incoming = {}, { withinForm = false } = {}) {
    const merged = merge({}, existing, incoming);
    copyEnumerableSymbolDescriptors(merged, existing, incoming);

    // Combine x-forAction arrays from both schemas
    const existingForActions = normalizeActions(existing['x-forAction']);
    const incomingForActions = normalizeActions(incoming['x-forAction']);
    const allForActions = uniq([...existingForActions, ...incomingForActions]).sort();
    if (allForActions.length) {
        merged['x-forAction'] = allForActions;
    }

    // Preserve resolved action sets: x-forAction also contains optional appearances.
    // Inferring again from that union would turn create-only requirements into update requirements.
    const requiredActions = uniq([...getRequiredActions(existing), ...getRequiredActions(incoming)]).sort();
    const hasDeclaration = [existing, incoming].some(
        schema => schema['x-required'] !== undefined || schema['x-requiredActions'] !== undefined
    );
    if (hasDeclaration) {
        merged['x-requiredActions'] = requiredActions;
        merged['x-required'] = requiredActions.length > 0;
        const requiredForAllActions =
            requiredActions.includes('*') || (requiredActions.includes('create') && requiredActions.includes('update'));
        // A single form can require create-only inputs. Across forms only shared requirements apply.
        merged['x-optional'] = requiredForAllActions
            ? false
            : withinForm
              ? [existing, incoming].some(
                    schema =>
                        schema.readOnly !== true && schema['x-optional'] === true
                )
              : true;
    }
    if ([existing, incoming].some(schema => schema['x-editable'] === true)) {
        merged['x-editable'] = true;
        delete merged.readOnly;
    }
    normalizeTypeBoxOptionalSymbol(merged, existing, incoming);

    return merged;
}

function getRequiredActions(schema) {
    if (schema['x-requiredActions'] !== undefined) return normalizeActions(schema['x-requiredActions']);
    if (!schema['x-required']) return [];
    const actions = normalizeActions(schema['x-forAction']);
    return actions.length ? actions : ['*'];
}

module.exports = { mergeSchemaFlags, isRequiredForAllCanonicalContexts };
